import { ModalService } from '@/components/fpComponents/services/ModalService/ModalService';
import type { MessageDataObject } from './useIframeConnection';
import { computed } from 'vue';
import GoToService from '@/router/GoToService';
import { useBreakpointsApi } from '@/composables/useBreakpoints';
import { useNewClientModal } from './useNewClientModal';
import Deal, { DealCreationMode } from '@/models/Deal';
import { SimulationDemand } from '@/models/Demand';
import {
  IFRAME_MESSAGE_TYPES,
  type NewDealAddNewClientPayload,
  type NewDealModalClosePayload,
  type SaveOnQuickDealPayload,
  iframeBridge,
} from './iframeBridgeProtocol';
import { Routes } from '@/router/Routes';
import { useIframeBridge } from './useIframeBridge';
import { buildIframeQueryString, clientTypeFromIsCompany } from './iframeModalQuery';
import DealsApi from '@/modules/deals/services/DealsApi';
import { usePreloadedIframe } from './usePreloadedIframe';
import type { filterStatateStoreStateForApi } from '@/modules/realEstates/filters';

type NewDealModalResponse = NewDealModalClosePayload & {
  deal?: Deal;
};

interface OpenNewDealModalOptions {
  mode?: DealCreationMode;
  flowTitle?: string;
  simulationId?: Nullable<string>;
  modeProductType?: Nullable<string>;
  selectedOfferIds?: Array<number | string>;
  realEstateOfferIds?: string[];
  realEstateFilters?: Nullable<Partial<filterStatateStoreStateForApi>>;
  userInput?: SimulationDemand;
  dealId?: number;
  skipProductTypeSelection?: boolean;
  // A client added inside the modal finishes in the client-creation wizard, whose close never reaches this modal's awaiter.
  onClientCreationClose?: (response?: Nullable<NewDealModalClosePayload>) => void;
}

export const useNewDealModal = (modalService?: ModalService) => {
  const { isPreloadedIframeAvailable, openModal } = usePreloadedIframe();

  const iframeMessageCallback = async (
    eventData: MessageDataObject<NewDealAddNewClientPayload | SaveOnQuickDealPayload>,
    options?: OpenNewDealModalOptions,
  ) => {
    if (iframeBridge.toFincrm.newDeal.addNewClient.matches(eventData)) {
      const { openAddClientModal } = useNewClientModal(modalService);
      await openAddClientModal({
        clientType: clientTypeFromIsCompany(eventData.data?.isCompany),
        parentModalType: IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL,
        mode: eventData.data?.mode,
        selectExistingClient: true,
        onClientCreationClose: options?.onClientCreationClose,
      });
    } else if (iframeBridge.toFincrm.newDeal.saveOnQuickDeal.matches(eventData) && eventData.data?.dealId) {
      try {
        await DealsApi.setNewStep(eventData.data?.dealId, Routes.DEALS.WIZARD.CHOOSE_OFFERS);
      } catch (error) {
        console.error('Error setting new step', error);
      } finally {
        GoToService.deal(eventData.data.dealId, Routes.DEALS.WIZARD.CHOOSE_OFFERS, Routes.DEALS.DEAL_DETAILS_TAB.PROCESS);
        // useModalStore only tracks legacy iframe modals, so it cannot close the
        // preloaded one — and skipping its close skips the RESET that wipes the
        // child's state for the next modal.
        const { modalClose } = useIframeBridge();
        const { savedRealEstateOfferIds } = eventData.data;
        modalClose.closeNewDealModal(savedRealEstateOfferIds?.length ? { savedRealEstateOfferIds } : null);
      }
    }
  };

  const openNewDealModal = async (clientId?: number, options?: OpenNewDealModalOptions) => {
    const modalOptions = {
      persistent: true,
      maxWidth: 962,
      contentClass: isMobile.value ? '' : 'delegation-modal-content',
      fullscreen: isMobile.value,
    };
    const messageCallback = (eventData: MessageDataObject) => iframeMessageCallback(eventData, options);

    if (isPreloadedIframeAvailable.value) {
      const preloadedRoute = `/deals/newDealModal${clientId ? `/${clientId}` : ''}`;
      return (await openModal(
        IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL,
        preloadedRoute,
        {},
        modalOptions,
        messageCallback,
        getNewDealModalQuery(options),
      )) as NewDealModalResponse | undefined;
    }

    const modalUrl = getNewDealModalUrl(clientId, options);
    return (await modalService?.openIframeModal({
      url: modalUrl,
      name: IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL,
      callback: messageCallback,
    }, modalOptions)) as NewDealModalResponse | undefined;
  };

  const getNewDealModalUrl = (clientId?: number, options?: OpenNewDealModalOptions) => {
    const route = clientId ? `deals/newDealModal/${clientId}` : 'deals/newDealModal';
    const queryString = buildIframeQueryString({
      mode: options?.mode,
      flowTitle: options?.flowTitle,
      simulationId: options?.simulationId,
      modeProductType: options?.modeProductType,
      selectedOfferIds: options?.selectedOfferIds?.length
        ? options.selectedOfferIds.join(',')
        : undefined,
      realEstateOfferIds: options?.realEstateOfferIds?.length
        ? options.realEstateOfferIds.join(',')
        : undefined,
      realEstateFilters: options?.realEstateFilters,
      userInput: options?.userInput,
      dealId: options?.dealId,
      skipProductTypeSelection: options?.skipProductTypeSelection,
    });
    return GoToService.getLendiV3Url(`${route}${queryString}`, true);
  };

  const getNewDealModalQuery = (options?: OpenNewDealModalOptions): Record<string, unknown> => {
    const query: Record<string, unknown> = {};
    if (options?.mode) query.mode = options.mode;
    if (options?.flowTitle) query.flowTitle = options.flowTitle;
    if (options?.simulationId) query.simulationId = options.simulationId;
    if (options?.modeProductType) query.modeProductType = options.modeProductType;
    if (options?.selectedOfferIds?.length) {
      query.selectedOfferIds = options.selectedOfferIds.join(',');
    }
    if (options?.realEstateOfferIds?.length) {
      query.realEstateOfferIds = options.realEstateOfferIds.join(',');
    }
    if (options?.realEstateFilters) query.realEstateFilters = JSON.stringify(options.realEstateFilters);
    if (options?.userInput) query.userInput = JSON.stringify(options.userInput);
    if (options?.dealId) query.dealId = String(options.dealId);
    return query;
  };

  const breakpoints = useBreakpointsApi();
  const isMobile = computed<boolean>(() => breakpoints.smAndDown);

  return {
    iframeMessageCallback,
    openNewDealModal,
    getNewDealModalUrl,
    getNewDealModalQuery,
  };
};
